import React, { createContext, useState } from 'react';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';

type ScreenShot = {
  id: number;
  image: string;
  note: string;
};

type ScreenShotsContextType = {
  screenShots: Array<ScreenShot>;
  fetchScreenShots: () => Promise<void>;
  takePicture: (identity: string) => Promise<void>;

  screenShotDrawerOpen: boolean;
  setScreenShotDrawerOpen: (value: boolean) => void;
  message: string;
  setMessage: (value: string) => void;
  openDrawer: (id: number) => void;
  screenShot: ScreenShot | undefined;

  setScreenShotFlag: () => Promise<void>;
  maybeTakeScreenShot: (identity: string) => Promise<void>;
  maybeFetchScreenShot: () => Promise<void>;

  notifOpen: boolean;
  setNotifOpen: (value: boolean) => void;
};

export const ScreenShotContext = createContext<ScreenShotsContextType>(null!);

export const ScreenShotsProvider: React.FC = ({ children }) => {
  const [screenShots, setScreenShots] = useState<Array<ScreenShot>>([]);
  const [screenShot, setScreenShot] = useState<ScreenShot>();
  const [screenShotDrawerOpen, setScreenShotDrawerOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [notifOpen, setNotifOpen] = useState(false);

  const { room } = useVideoContext();

  const fetchScreenShots = async () => {
    const endpoint = `${process.env.REACT_APP_TOKEN_ENDPOINT}/work-orders/call/screen-shots?room=${room?.name}`;

    const res = await fetch(endpoint, {
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('portal_token'),
      },
    });

    if (res.status === 200) {
      const data = await res.json();
      setScreenShots(data.result);
    }
  };

  const takeScreenShot = (identity: string) => {
    const listVideoEl = document.getElementsByTagName('video');
    if (listVideoEl.length === 0) return;

    const shots = [] as Array<{ image: string; user: string }>;

    for (const el of listVideoEl) {
      const videoIdentity = el.getAttribute('data-identity');

      if (identity !== videoIdentity) continue;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) return;

      const w = el.videoWidth;
      const h = el.videoHeight;

      if (!w || !h) continue;

      canvas.width = w;
      canvas.height = h;
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(el, 0, 0, w, h);

      shots.push({ image: canvas.toDataURL(), user: el.getAttribute('data-name') ?? '' });
    }

    return shots;
  };

  const takePicture = async (identity: string) => {
    try {
      const imageBase64s = takeScreenShot(identity) ?? [];

      for (const imageBase64 of imageBase64s) {
        const endpoint = `${process.env.REACT_APP_TOKEN_ENDPOINT}/work-orders/call/screen-shots?room=${room?.name}`;

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            accept: 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('portal_token'),
          },
          body: JSON.stringify({ image: imageBase64.image.substring(22), user: imageBase64.user }),
        });

        if (res.status === 200) fetchScreenShots();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const setScreenShotFlag = async () => {
    setNotifOpen(true);
    setTimeout(() => setNotifOpen(false), 3000);

    const endpoint = `${process.env.REACT_APP_TOKEN_ENDPOINT}/work-orders/call/screenshot-flag?room=${room?.name}`;

    await fetch(endpoint, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('portal_token'),
      },
      body: JSON.stringify({ flag: true }),
    });
  };

  const maybeTakeScreenShot = async (identity: string) => {
    const endpoint = `${process.env.REACT_APP_TOKEN_ENDPOINT}/work-orders/call/get-take-screenshot-flag?room=${room?.name}`;

    while (true) {
      try {
        const res = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
            accept: 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('portal_token'),
          },
        });

        if (res.ok) {
          const data = await res.json();

          if (data.result?.flag) {
            await takePicture(identity);
          }
        }
      } catch (e) {
        console.error(e);
      }

      await new Promise(resolve => setTimeout(resolve, 200));
    }
  };

  const maybeFetchScreenShot = async () => {
    const endpoint = `${process.env.REACT_APP_TOKEN_ENDPOINT}/work-orders/call/get-fetch-screenshot-flag?room=${room?.name}`;

    while (true) {
      try {
        const res = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
            accept: 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('portal_token'),
          },
        });

        if (res.ok) {
          const data = await res.json();

          if (data.result?.flag) {
            await fetchScreenShots();
          }
        }
      } catch (e) {
        console.error(e)
      }

      await new Promise(resolve => setTimeout(resolve, 200));
    }
  };

  const openDrawer = (id: number) => {
    const s = screenShots.find(sc => sc.id === id);
    if (!s) return;

    setScreenShot(s);
    setMessage(s.note);
    setScreenShotDrawerOpen(true);
  };

  return (
    <ScreenShotContext.Provider
      value={{
        screenShots,
        fetchScreenShots,
        takePicture,
        screenShotDrawerOpen,
        setScreenShotDrawerOpen,
        message,
        setMessage,
        openDrawer,
        screenShot,
        setScreenShotFlag,
        maybeTakeScreenShot,
        maybeFetchScreenShot,
        notifOpen,
        setNotifOpen,
      }}
    >
      {children}
    </ScreenShotContext.Provider>
  );
};
